/**
 * Subscription Adapters
 *
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * Adapters for Stripe subscription data transformation.
 *
 * FCR S291 Issue 9 (DD-3): Stripe field reads use the dual-path helpers
 * in ./_stripe-helpers.ts so the adapter works against both legacy
 * (≤2025-12) and API 2026-04-22.dahlia shapes. Direct ``line.price.product``
 * / ``order.payment_intent`` reads return null under v15 and would surface
 * "No license key" on every subscription card.
 */

import { TSL_TRIAL_PRODUCT } from "@/lib/constants";
import { getTslFeaturesToStripe, getTslPaidProducts } from "@/services/productIdConfig";
import {
  filterLicensesByProduct,
} from "@/lib/license-matching";
import { formatCurrency, formatDate, getProductDisplayName } from "@/lib/utils";
import { ordersApi } from "@/redux/services/orders";
import { store, type AppDispatch } from "@/redux/store";
import { License, StripeCustomer } from "@/types";
import {
  fetchLicense,
  fetchLicenses,
  fetchProductName,
} from "@/utils/dashboardUtils";
import { logger } from "@/lib/logger";
import type { ActiveSubscription, SubscriptionProduct, Product } from "./adapter-types";
import {
  getInvoicePaymentIntent,
  getLineProductId,
  getLineInterval,  // FCR S523 Issue 4: needed for synthetic row display name
} from "./_stripe-helpers";

/**
 * FCR S523 Issue 4: Shared helper for building the display name, interval, and
 * amount for a synthetic "Past Subscription" / "Past Product" row.
 *
 * Active subscription rows route through fetchProductName() + getProductDisplayName()
 * and render correctly. Synthetic rows for refunded products previously read
 * matchingLine.description directly — a Stripe-formatted receipt string like
 * "1 × Dictation Pro v2 (at $69.00 / year)" — instead of the clean product name.
 *
 * This helper routes synthetic rows through the same naming pipeline so every
 * refunded row shows "Dictation Pro v2 - Annual License" with the correct badge
 * and price, regardless of which invoice it came from.
 *
 * Used in both synthetic-row sites (buildSyntheticRefundedSubscriptionRows for
 * Billing and adaptSubscriptionProducts for My Products) so they can never drift.
 */
async function resolveSyntheticProductDisplay(
  pid: string,
  matchingLine: { description?: string; amount?: number } | undefined,
  currency: string,
): Promise<{ productName: string; interval: string; amount: number; amountDisplay: string }> {
  const lineInterval = getLineInterval(matchingLine as Parameters<typeof getLineInterval>[0]) ?? "";
  const baseName = await fetchProductName(pid);
  const productName = getProductDisplayName(
    baseName ?? "Subscription",
    lineInterval ? "recurring" : undefined,
    lineInterval || undefined,
    pid,
  );
  const amount = (matchingLine as { amount?: number } | undefined)?.amount ?? 0;
  return {
    productName,
    interval: lineInterval,
    amount,
    amountDisplay: amount > 0 ? formatCurrency(currency, amount) : "",
  };
}

export const adaptActiveSubscription = async (
  stripeCustomer: StripeCustomer,
  orders?: unknown[],
): Promise<ActiveSubscription[]> => {
  // S-3 (FCR S509 Pass 10): a customer with zero remaining Stripe
  // subscriptions (e.g. their only subscription product was refunded) must
  // still see a synthetic Past Subscription row for that refund -- so this
  // early return now falls through to the synthetic-row builder below
  // instead of returning [] immediately.
  const hasNoLiveSubscriptions =
    !stripeCustomer ||
    !stripeCustomer.subscriptions ||
    !stripeCustomer.subscriptions.data ||
    stripeCustomer.subscriptions.data.length === 0;

  if (hasNoLiveSubscriptions) {
    logger.info("No subscriptions found for customer - checking for refunded products only");
    return buildSyntheticRefundedSubscriptionRows([], orders);
  }

  const getPaymentIntent = async (
    productId: string,
    subscriptionId: string,
  ) => {
    // SK-2 (FCR S323): Two-pass search that always prefers the subscription_create
    // invoice.  QLM stores licenses under the payment intent from the original
    // checkout invoice (billing_reason: "subscription_create").  After any renewal
    // Stripe creates a new invoice (billing_reason: "subscription_cycle") with a
    // different payment intent.  Stripe returns invoices newest-first, so the single-
    // pass loop used to return the renewal PI — causing "No license key" on the
    // dashboard for every subscriber after their first renewal.
    try {
      const ordersQuery = ordersApi.endpoints.getOrders.initiate();
      const ordersResult = await (store.dispatch as AppDispatch)(ordersQuery);
      const orders = ordersResult.data;

      if (!orders || !Array.isArray(orders)) {
        return "";
      }

      let fallback = "";

      for (const order of orders) {
        if (!order?.lines?.data || !Array.isArray(order.lines.data)) {
          continue;
        }

        for (const line of order.lines.data) {
          // FCR S291 Issue 9: dual-path productId resolution. Direct
          // line.price.product is null under v15.
          const lineProductId = getLineProductId(line);
          if (!lineProductId || !line?.parent) {
            continue;
          }

          if (
            lineProductId === productId &&
            line.parent.subscription_item_details?.subscription_item ===
              subscriptionId
          ) {
            // FCR S291 Issue 9: order.payment_intent is null under v15;
            // walk the new payments.data[0].payment.payment_intent path.
            const pi = getInvoicePaymentIntent(order) || "";
            if (order.billing_reason === "subscription_create") {
              // Always prefer the original checkout invoice — QLM licensed under this PI.
              return pi;
            }
            if (!fallback) {
              // Keep first renewal PI as last-resort (e.g. migrated subscriptions
              // with no subscription_create invoice in the returned history).
              fallback = pi;
            }
          }
        }
      }

      return fallback;
    } catch (error) {
      logger.error("Error fetching payment intent:", error);
    }

    return "";
  };

  // Defensive optional chaining: the hasNoLiveSubscriptions early-return above
  // already guarantees subscriptions.data is non-empty at runtime by this
  // point, but TypeScript's control-flow narrowing does not carry through an
  // intermediate boolean variable (hasNoLiveSubscriptions), so this guards
  // against TS18048 without changing behavior.
  const subscriptionPromises = (stripeCustomer.subscriptions?.data ?? []).map(
    (subscription) =>
      Promise.all(
        subscription.items.data.map(async (item) => {
          // S-12 (FCR S323): Use item subtotal × effective tax rate rather than the
          // full latest_invoice.amount_paid. When a customer buys an annual subscription
          // alongside a lifetime license in the same checkout, amount_paid reflects the
          // combined invoice total — inflating the "Next Payment" widget beyond the actual
          // subscription renewal amount. item.price.unit_amount × quantity is the correct
          // subscription-only base; then we scale by the effective tax rate from that same
          // invoice so tax-jurisdiction customers see a tax-inclusive renewal estimate.
          const itemSubtotal =
            (item.price.unit_amount ?? 0) * (item.quantity ?? 1);

          let chargedAmount = itemSubtotal;

          const inv = subscription.latest_invoice;
          const invSubtotal = inv?.subtotal ?? 0;
          const invTax = inv?.tax ?? 0;
          if (invSubtotal > 0 && invTax > 0) {
            const effectiveTaxRate = invTax / invSubtotal;
            chargedAmount = Math.round(itemSubtotal * (1 + effectiveTaxRate));
          }

          if (chargedAmount === 0) {
            logger.warn(
              `[subscription-adapter] chargedAmount is 0 for ` +
                `subscription ${subscription.id} / item ${item.id}. ` +
                `Check that price.unit_amount is set on the Stripe item.`,
            );
          }

          const itemPaymentIntent = await getPaymentIntent(
            item.price.product,
            item.id,
          );
          // R2-2 C: Stripe keeps the subscription "active" because QLM expiry is a
          // manual server-side operation invisible to Stripe. Cross-reference the
          // QLM license state so the row can show "Expired" and suppress Cancel.
          //
          // S-2 (FCR S509 Pass 10): use .every(), not .some(). A multi-product
          // subscription sharing one payment intent can return more than one
          // license under fetchLicense -- after a per-product refund revokes
          // the refunded product's key, .some() would mark the KEPT product
          // expired too, just because its expired sibling shows up in the
          // same lookup. A product is genuinely expired only when every
          // license resolved for it is expired.
          let qlmExpired = false;
          try {
            const lic = await fetchLicense(
              itemPaymentIntent,
              item.price.product,
            );
            const itemLicenses = Array.isArray(lic) ? lic : [];
            qlmExpired =
              itemLicenses.length > 0 &&
              itemLicenses.every(
                (l) =>
                  Boolean(l.IsExpired) ||
                  l.ActivationStatus?.toLowerCase().includes("expired") === true,
              );
          } catch (error) {
            logger.error(
              `Failed to fetch QLM license state for item ${item.id}:`,
              error,
            );
          }

          return {
            id: item.id,
            renewalDate: formatDate(item.current_period_end),
            startDate: formatDate(item.current_period_start),
            // A-4: raw Unix seconds for newest-first sort; formatDate loses the precision
            currentPeriodStart: item.current_period_start ?? 0,
            active: item.plan.active,
            amountDisplay: formatCurrency(
              item.price.currency,
              chargedAmount,
            ),
            amount: chargedAmount,
            currency: item.price.currency,
            productId: item.price.product,
            // S256 (FCR §3): route productName through getProductDisplayName
            // so the Subscriptions row shows the same suffix as every other
            // surface. Subscriptions are always `type: "recurring"` by
            // definition; interval comes from the Stripe plan object.
            // SS-6 (FCR Pass 14): Derive a priceLabel from the price nickname
            // or word_count metadata so TSL subscriptions show the full plan
            // name (e.g. "Text Speaker Live v4 - 45,000 Words - Monthly")
            // rather than the bare product name. All three dashboard surfaces
            // (Overview, Next Payment, License Details) read from this adapter,
            // so fixing it here corrects all three at once.
            // NOTE: priceLabel is computed with await before the object literal
            // (extracted below) to avoid assigning a Promise<string> to
            // productName, which would happen if await were used inside a
            // non-async IIFE. The value is resolved here before the object spread.
            productName: await (async () => {
              const baseProductName = await fetchProductName(item.price.product);
              const wordCountMeta = (item.plan as { metadata?: Record<string, string> })?.metadata?.word_count;
              const wordCountLabel = wordCountMeta
                ? `${Number(wordCountMeta).toLocaleString()} Words`
                : "";
              const planNickname = (item.plan as { nickname?: string })?.nickname?.trim() ?? "";
              const priceLabel = planNickname
                ? planNickname
                : wordCountLabel
                  ? `${baseProductName} - ${wordCountLabel}`
                  : "";
              return getProductDisplayName(
                baseProductName,
                "recurring",
                item.plan.interval,
                item.price.product,
                priceLabel || undefined,
              );
            })(),
            paymentIntent: itemPaymentIntent,
            qlmExpired,
            interval: item.plan.interval,
            quantity: item.quantity ?? 1,
            status: subscription.status,
            cancelAtPeriodEnd: subscription.cancel_at_period_end,
            // S347: The Stripe Subscription ID (sub_xxx) is required by the
            // Customer Portal subscription_cancel flow. `id` above is the
            // SubscriptionItem ID (si_xxx) — a different object entirely.
            subscriptionId: subscription.id,
            // B10/S390: Price ID for upgrade routing (Annual→Lifetime map is price-keyed)
            priceId: item.price.id,
            // Bug 3 (FCR Pass 6): pass raw Unix timestamps; do not format here.
            // The portal often sets cancel_at without setting cancel_at_period_end,
            // so detection logic in subscription-row must inspect both fields.
            // canceledAt distinguishes "scheduled" from "already ended".
            cancelAt: subscription.cancel_at ?? null,
            canceledAt: subscription.canceled_at ?? null,
            // SK-2 (FCR Pass 8): Flag upgrade-driven cancellations so the
            // Reactivate button is hidden. Two markers are checked:
            // • upgrade_to_lifetime — set by the Annual→Lifetime webhook
            // • tsl_plan_upgrade — set by the 45K→100K PU-1 cancel (S3 fix)
            // Both mean the cancellation was intentional and resuming billing
            // would create a double-charge.
            cancelledDueToUpgrade:
              subscription.metadata?.["upgrade_to_lifetime"] === "true" ||
              subscription.metadata?.["tsl_plan_upgrade"] === "true",
          };
        }),
      ),
  );

  const nestedSubscriptions = await Promise.all(subscriptionPromises);
  const liveRows = nestedSubscriptions.flat();

  // S-3 (FCR S509 Pass 10): append a synthetic Past Subscription row for
  // each refunded product not already represented by a live row above.
  const syntheticRows = await buildSyntheticRefundedSubscriptionRows(liveRows, orders);

  return [...liveRows, ...syntheticRows];
};

/**
 * S-3 (FCR S509 Pass 10): Build synthetic "Past Subscription" rows for
 * products that were refunded on an invoice but no longer have a live
 * Stripe subscription representing them on the Billing page.
 *
 * Mirrors the same synthetic-row pattern already used by
 * ``adaptSubscriptionProducts`` for the My Products page (see
 * ``syntheticRefundedRows`` below) -- but built against ``ActiveSubscription``'s
 * shape instead of ``Product``'s, since the Billing page's
 * ``ActiveSubscriptionsTable`` consumes ``adaptActiveSubscription``'s output
 * directly, not the My Products adapter.
 *
 * Root cause this closes: per-product refunds on a multi-product invoice
 * (S-2) can leave the refunded product's Stripe subscription item deleted
 * (not cancelled) with no trace in ``stripeCustomer.subscriptions`` at all.
 * Without a synthetic row, the customer has no record the product was ever
 * billed -- it simply vanishes from Billing. A refunded product on a
 * lifetime / one-time invoice never had a Stripe subscription to begin
 * with and has the same problem.
 */
/**
 * FCR Pass 13 Sakshi Issue 2: Deduplication key for refunded past rows.
 *
 * Previous code keyed on product ID alone, which caused a refunded row to be
 * suppressed whenever the same product was repurchased (both share the same
 * productId, so the second live purchase "covered" the old refunded one).
 *
 * The correct key is product + payment intent (one purchase). A new purchase
 * has a different PI, so it no longer erases the refund history of the old one.
 * When no PI is available (edge case: very old invoices), fall back to
 * product + invoice creation timestamp as a stable surrogate.
 */
function refundedPastRowKey(productId: string, paymentIntent: string, invoiceCreated = 0): string {
  if (paymentIntent) return `${productId}:${paymentIntent}`;
  return `${productId}:inv-${invoiceCreated}`;
}

/**
 * Build the set of already-covered refunded-purchase keys from live rows so
 * we only suppress a synthetic row when the *exact same purchase* (same
 * productId + paymentIntent) is already represented by a canceled Stripe row.
 * A new active purchase of the same product does NOT cover the old refunded one.
 */
function buildCoveredRefundedPastKeys(liveRows: ActiveSubscription[]): Set<string> {
  const keys = new Set<string>();
  for (const row of liveRows) {
    // Only canceled Stripe subscription rows represent prior purchases --
    // active rows are the new purchase and must not suppress refund history.
    if (row.status !== "canceled" && !row.qlmExpired) continue;
    if (!row.paymentIntent) continue;
    keys.add(refundedPastRowKey(row.productId, row.paymentIntent));
  }
  return keys;
}

async function buildSyntheticRefundedSubscriptionRows(
  liveRows: ActiveSubscription[],
  orders?: unknown[],
): Promise<ActiveSubscription[]> {
  if (!orders || !Array.isArray(orders) || orders.length === 0) return [];

  // FCR Pass 13 Sakshi Issue 2: dedupe by product+paymentIntent (purchase level),
  // not just by productId. A new active repurchase no longer hides old refund history.
  const coveredKeys = buildCoveredRefundedPastKeys(liveRows);
  // Still track productId+invoicePI combos we've emitted to avoid duplicate synthetic rows
  // when the same product is refunded on more than one invoice.
  const emittedKeys = new Set<string>();

  // Gather all products that need a synthetic row before resolving names, so we
  // can run the async display lookups in parallel (same pattern as active rows).
  const pending: {
    pid: string;
    paymentIntent: string;
    matchingLine: { description?: string; amount?: number } | undefined;
    createdAt: number;
    currency: string;
  }[] = [];

  for (const inv of orders) {
    const invoice = inv as {
      refunded_product_ids?: string[];
      lines?: { data?: unknown[] };
      created?: number;
      currency?: string;
      payment_intent?: string;
      payments?: { data?: Array<{ payment?: { payment_intent?: string } }> };
    };
    const refundedPids = invoice.refunded_product_ids ?? [];
    if (!Array.isArray(refundedPids) || refundedPids.length === 0) continue;

    // Read the invoice payment intent to form a purchase-level key.
    const invoicePI = getInvoicePaymentIntent(invoice) || "";
    const createdAt = invoice.created ?? 0;
    const lines: unknown[] = invoice.lines?.data ?? [];

    for (const pid of refundedPids) {
      const purchaseKey = refundedPastRowKey(pid, invoicePI, createdAt);
      // Skip if this exact purchase is already represented by a Stripe row.
      if (coveredKeys.has(purchaseKey)) continue;
      // Skip if we already added a synthetic row for this purchase this pass.
      if (emittedKeys.has(purchaseKey)) continue;
      emittedKeys.add(purchaseKey);

      const matchingLine = lines.find((l) => {
        const line = l as {
          price?: { product?: string };
          pricing?: { price_details?: { product?: string } };
        };
        return (
          line?.price?.product === pid ||
          line?.pricing?.price_details?.product === pid
        );
      }) as { description?: string; amount?: number } | undefined;

      pending.push({ pid, paymentIntent: invoicePI, matchingLine, createdAt, currency: invoice.currency ?? "usd" });
    }
  }

  // FCR Pass 13 Sakshi Issue 2 (additional): resolve display info in parallel,
  // matching the approach already used for active subscription rows.
  const resolved = await Promise.all(
    pending.map((x) => resolveSyntheticProductDisplay(x.pid, x.matchingLine, x.currency)),
  );

  const syntheticRows: ActiveSubscription[] = pending.map((x, i) => {
    const { productName, interval, amount, amountDisplay } = resolved[i];
    return {
      id: `refunded-${x.pid}-${x.paymentIntent || x.createdAt}`,
      renewalDate: "",
      startDate: x.createdAt ? formatDate(x.createdAt) : "",
      interval,
      active: false,
      amountDisplay,
      amount,
      currency: x.currency,
      // Store the paymentIntent so dedup logic in future passes can use it.
      paymentIntent: x.paymentIntent,
      productId: x.pid,
      productName,
      // status === "canceled" routes this to Past Subscriptions in the Billing
      // table. qlmExpired true and active false reflect that the license was
      // revoked by the refund.
      status: "canceled",
      qlmExpired: true,
    };
  });

  return syntheticRows;
}

export const adaptSubscriptionProducts = async (
  activeSubscriptions: ActiveSubscription[],
  orders?: unknown[],
): Promise<SubscriptionProduct[]> => {
  if (!activeSubscriptions || activeSubscriptions.length === 0) {
    return [];
  }

  // CHANGE_06 (FCR S483 Bugs 2-3): Split into active and cancelled subscriptions.
  // Previously cancelled subs were filtered out entirely (S477) leaving the customer
  // with no record on the Products tab after cancellation — they had to check
  // Billing to understand why the product disappeared. Now cancelled subscriptions
  // produce a Product entry with displayStatus="expired" so the customer always
  // sees what they had and can use the "Buy again" link.
  const billableSubscriptions = activeSubscriptions.filter(
    (sub) => sub.status !== "canceled",
  );
  const cancelledSubscriptions = activeSubscriptions.filter(
    (sub) => sub.status === "canceled",
  );

  if (billableSubscriptions.length === 0 && cancelledSubscriptions.length === 0) {
    return [];
  }

  // NF-3: Group by productId:priceId to keep Annual and Lifetime (or different tiers) separate.
  // Grouping by productId only merges them on the dashboard after SK-U migration.
  const groups = new Map<string, ActiveSubscription[]>();
  for (const sub of billableSubscriptions) {
    const priceId = (sub as ActiveSubscription & { priceId?: string }).priceId || "";
    const groupKey = priceId ? `${sub.productId}:${priceId}` : sub.productId;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(sub);
  }

  const productPromises = Array.from(groups.values()).map(async (group) => {
    const productId = group[0].productId;

    const licensesNested: License[][] = await Promise.all(
      group.map(async (item: ActiveSubscription) => {
        try {
          const licenses = await fetchLicense(item.paymentIntent, productId);
          return Array.isArray(licenses) ? licenses : [];
        } catch (error) {
          logger.error(
            `Failed to fetch license for payment intent ${item.paymentIntent}:`,
            error,
          );
          return [];
        }
      }),
    );

    const productName = group[0].productName;
    let totalDevices = 0;
    let availableDevices = 0;

    // Build per-license quantity map before flattening.
    // group[i] is the subscription purchase that generated licensesNested[i].
    // Using group[0].quantity for all licenses is incorrect when a user has
    // multiple purchases of the same product with different quantities.
    // The TSL trial path below has its own quantity logic and is unaffected.
    const licenseQuantityMap = new Map<string, number>();
    licensesNested.forEach((licensesForPurchase, i) => {
      const qty = group[i].quantity ?? 1;
      licensesForPurchase.forEach((l) => {
        if (l.ActivationKey) licenseQuantityMap.set(l.ActivationKey, qty);
      });
    });

    // Handle TSL trial product separately
    if (productId === TSL_TRIAL_PRODUCT) {
      try {
        const allLicenses: License[] = await fetchLicenses();
        const matchResult = filterLicensesByProduct(allLicenses, productId);
        const licenses = matchResult.licenses;

        if (matchResult.legacyMatchCount > 0) {
          logger.info(
            `[TSL Trial] ${matchResult.legacyMatchCount} legacy license(s) matched by ProductID`,
          );
        }

        licenses.forEach((license) => {
          // F-9 (S343): Use QLM's MaxActivations as authoritative source.
          const maxActivations = license.MaxActivations ?? license.NumLicenses ?? 1;
          totalDevices += maxActivations;
          // F-9 (S343): Clamp availableDevices — QLM can temporarily exceed NumLicenses.
          availableDevices += Math.max(0,
            license.AvailableLicenses ?? Math.max(0, maxActivations - (license.ActivationCount ?? 0)));
        });

        const totalLicensesCount = licenses.reduce(
          (sum, license) => sum + (license.NumLicenses ?? 1),
          0,
        );

        return {
          id: group[0].id,
          productId: productId,
          productName: productName,
          licenses: licenses,
          totalLicenses: totalLicensesCount,
          interval: group[0].interval,
          totalDevices: totalDevices,
          availableDevices: availableDevices,
        };
      } catch (error) {
        logger.error(
          `Failed to process TSL trial product ${productId}:`,
          error,
        );
        return {
          id: group[0].id,
          productId: productId,
          productName: productName,
          licenses: [],
          totalLicenses: 0,
          interval: group[0].interval,
          totalDevices: 0,
          availableDevices: 0,
        };
      }
    }

    // Process regular subscriptions - combine licenses from ALL purchases
    const flatLicenses = licensesNested.flat();
    const totalProductsInOrder = Math.max(flatLicenses.length, 1);
    let matchResult = filterLicensesByProduct(flatLicenses, productId, totalProductsInOrder);
    let licenses = matchResult.licenses;

    // SK-1 (S306): TSL plan-upgrade fallback.
    // After a 45K→100K upgrade, the QLM record's OrderID/AffiliateID is set at
    // original purchase and intentionally not updated on upgrade — so the
    // upgrade-invoice paymentIntent returns 0 rows. Fall back to all-user-licenses
    // and re-match by Features field (the authoritative post-upgrade plan-tier indicator).
    // Safe: TSL_FEATURES_TO_PRODUCT_MAP keys are product-specific, so a 100K license
    // ("0:4;") will never match a 45K product card.
    if (licenses.length === 0 && getTslPaidProducts().has(productId)) {
      try {
        const allLicenses = await fetchLicenses();
        const fallbackResult = filterLicensesByProduct(allLicenses, productId);
        if (fallbackResult.licenses.length > 0) {
          matchResult = fallbackResult;
          licenses = fallbackResult.licenses;
          logger.info(
            `[Subscription ${productId}] TSL upgrade fallback: found ${licenses.length} license(s) via all-user fetch`,
          );
        }
      } catch (error) {
        logger.error(`[Subscription ${productId}] TSL upgrade fallback failed:`, error);
      }
    }

    // If no matching licenses found from payment-intent-scoped fetch, show 0
    // rather than widening the search to ALL user licenses via fetchLicenses().
    // The wider search caused cross-order contamination — licenses from
    // completely unrelated past orders could appear under the wrong product.
    if (licenses.length === 0 && flatLicenses.length > 0) {
      logger.warn(
        `[Subscription ${productId}] No matching licenses from payment intent ` +
          `(${flatLicenses.length} fetched, 0 matched). ` +
          `Check UserData1 population in QLM.`,
      );
    }

    if (matchResult.legacyMatchCount > 0) {
      logger.info(
        `[Subscription ${productId}] ${matchResult.legacyMatchCount} legacy license(s) matched by ProductID`,
      );
    }

    licenses.forEach((license) => {
      // F-9 (S343): QLM's MaxActivations is authoritative — it already includes
      // the +1 applied at license creation (num_seats = quantity + 1).
      // Using raw Stripe quantity here loses that +1 and produces the wrong total.
      const stripeQty = licenseQuantityMap.get(license.ActivationKey) ?? 1;
      const maxActivations =
        license.MaxActivations ??
        license.NumLicenses ??
        (stripeQty > 1 ? stripeQty + 1 : 1);
      totalDevices += maxActivations;
      // F-9 (S343): Clamp to prevent negative available count when QLM temporarily
      // reports AvailableLicenses > NumLicenses right after license creation.
      availableDevices += Math.max(0,
        license.AvailableLicenses ?? Math.max(0, maxActivations - (license.ActivationCount ?? 0)));
    });

    const totalLicensesCount = licenses.reduce(
      (sum, license) => {
        const qty = licenseQuantityMap.get(license.ActivationKey) ?? 1;
        // F-9 (S343): Prefer QLM's NumLicenses (already includes +1);
        // fall back to Stripe qty + 1 only when QLM data is absent.
        return sum + (license.NumLicenses ?? (qty > 1 ? qty + 1 : 1));
      },
      0,
    );

    return {
      id: group[0].id,
      productId: productId,
      productName: productName,
      licenses: licenses,
      totalLicenses: totalLicensesCount,
      interval: group[0].interval,
      totalDevices: totalDevices,
      availableDevices: availableDevices,
      // A-4: most recent item's period start as Unix seconds for newest-first sort
      createdAt: (group[0] as { currentPeriodStart?: number }).currentPeriodStart ?? 0,
    };
  });

  const result = await Promise.all(productPromises);

  // Issue 1 (FCR Pass 11): Cross-card TSL dedupe — must run BEFORE SK-2 suppress.
  // Both TSL paid plans share QLM ProductID 22, so the matching layer can route a
  // license to the wrong card when StripeProductId is absent or stale. This pass
  // removes any license whose Features tier does not match the card it landed on,
  // then recomputes device totals so the card counts are accurate.
  // Non-TSL products are untouched (the Features lookup returns undefined for them).
  const deduped = result.map((card) => {
    if (!getTslPaidProducts().has(card.productId)) return card;
    const filteredLicenses = card.licenses.filter((license) => {
      const tier = license.Features
        ? getTslFeaturesToStripe()[license.Features.trim()]
        : undefined;
      if (tier !== undefined) return tier === card.productId; // TSL: tier decides
      // License has no Features — keep it on this card as a safe fallback.
      return true;
    });
    if (filteredLicenses.length === card.licenses.length) return card;
    // Recompute device totals from the filtered list so counts stay accurate.
    let totalDevices = 0;
    let availableDevices = 0;
    for (const lic of filteredLicenses) {
      const numLic = Number(lic.NumLicenses) || 0;
      const avail = Number(lic.AvailableLicenses) || 0;
      totalDevices += numLic;
      availableDevices += avail;
    }
    return {
      ...card,
      licenses: filteredLicenses,
      totalLicenses: filteredLicenses.length,
      totalDevices,
      availableDevices,
    };
  });

  // SK-2 (S306): Suppress empty TSL product cards that appear after a plan upgrade.
  // After a 45K→100K upgrade, Stripe preserves the prior subscription history so
  // both a 45K and 100K card are created from the grouper. Once SK-1 routes the
  // license to the 100K card, the 45K card renders empty. Suppress it — but only
  // when at least one other TSL card has licenses, so the page never goes fully blank.
  // Run on the deduped result so a card emptied by dedupe is also caught here.
  const tslCards = deduped.filter((c) => getTslPaidProducts().has(c.productId));
  const populatedTslCount = tslCards.filter((c) => c.licenses.length > 0).length;
  let activeResult: SubscriptionProduct[];
  if (populatedTslCount > 0) {
    activeResult = deduped.filter(
      (c) => !getTslPaidProducts().has(c.productId) || c.licenses.length > 0,
    );
  } else {
    activeResult = deduped;
  }

  // CHANGE_06 (FCR S483 Bugs 2-3): Build lightweight expired entries for
  // cancelled subscriptions so they appear on the Products tab with a status
  // label and a "Buy again" link. QLM licences are revoked so we don't attempt
  // to fetch them — the entry is display-only with empty license arrays.
  const expiredEntries: Product[] = cancelledSubscriptions
    .filter(
      (sub) =>
        // Deduplicate: skip if an active entry already covers this product+price.
        !activeResult.some(
          (r) =>
            r.productId === sub.productId &&
            (r as SubscriptionProduct & { priceId?: string }).priceId ===
              ((sub as ActiveSubscription & { priceId?: string }).priceId || ""),
        ),
    )
    .map((sub): Product => {
      // Format the access-end date from canceledAt or cancelAt timestamp.
      const endTs = sub.canceledAt ?? sub.cancelAt ?? null;
      const accessEndDate = endTs
        ? new Date(Number(endTs) * 1000).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : undefined;

      return {
        id: sub.id,
        productId: sub.productId,
        productName: sub.productName || "Subscription",
        licenses: [],
        totalLicenses: 0,
        type: "recurring",
        interval: sub.interval || "",
        totalDevices: 0,
        availableDevices: 0,
        // sub.createdAt never existed on ActiveSubscription -- the real Unix
        // timestamp is carried through as currentPeriodStart (set when this
        // object was built in adaptActiveSubscription above, line ~197),
        // smuggled past the public interface the same way the My Products
        // adapter already does a few lines above this one.
        createdAt:
          (sub as ActiveSubscription & { currentPeriodStart?: number })
            .currentPeriodStart ?? 0,
        displayStatus: "expired",
        accessEndDate,
        // S-2 / SU-4 (FCR Pass 14): Carry paymentIntent so the dedup key
        // productId+paymentIntent matches the synthetic refunded row.
        // Without this the guard in buildMyProductsCoveredKeys() skips every
        // expired Stripe row (all have no paymentIntent) and the synthetic
        // row is emitted alongside it -- producing two cards per product.
        paymentIntent: sub.paymentIntent || undefined,
      };
    });

  // SK-I4 (FCR S505): Build synthetic Past Subscription rows for products that
  // were refunded on an invoice (per-product refund). These differ from normal
  // cancelled subscriptions because the Stripe subscription was cancelled by
  // cancel_subscription_for_refunded_product and so already appears in
  // cancelledSubscriptions -- but a product refunded on a lifetime / one-time
  // invoice has no Stripe subscription and would be invisible without a synthetic
  // row. We scan orders for any refundedProductIds[] and add an "expired" entry
  // when that product is not already covered by activeResult or expiredEntries.
  const syntheticRefundedRows: Product[] = [];
  if (orders && Array.isArray(orders)) {
    // FCR Pass 13 Sakshi Issue 2: My Products path — same purchase-level dedup fix.
    // Build the covered-key set from active and expired entries. A new repurchase
    // of the same product is active, not canceled, so it will NOT cover the old
    // refunded purchase.
    const buildMyProductsCoveredKeys = (): Set<string> => {
      const keys = new Set<string>();
      for (const r of [...activeResult, ...expiredEntries]) {
        const rr = r as { productId?: string; paymentIntent?: string; displayStatus?: string };
        if (!rr.paymentIntent || !rr.productId) continue;
        // Only expired/canceled entries represent a prior purchase that is already shown.
        if (rr.displayStatus !== "expired") continue;
        keys.add(refundedPastRowKey(rr.productId, rr.paymentIntent));
      }
      return keys;
    };
    const coveredKeys = buildMyProductsCoveredKeys();
    const emittedKeys = new Set<string>();

    const myProductsPending: {
      pid: string;
      paymentIntent: string;
      matchingLine: { description?: string; amount?: number } | undefined;
      createdAt: number;
      currency: string;
    }[] = [];

    for (const inv of orders) {
      const invoice = inv as {
        refunded_product_ids?: string[];
        lines?: { data?: unknown[] };
        metadata?: Record<string, string>;
        created?: number;
        currency?: string;
        payment_intent?: string;
        payments?: { data?: Array<{ payment?: { payment_intent?: string } }> };
      };
      const refundedPids = invoice.refunded_product_ids ?? [];
      if (!Array.isArray(refundedPids) || refundedPids.length === 0) continue;

      const invoicePI = getInvoicePaymentIntent(invoice) || "";
      const createdAt = invoice.created ?? 0;
      const lines: unknown[] = invoice.lines?.data ?? [];

      for (const pid of refundedPids) {
        const purchaseKey = refundedPastRowKey(pid, invoicePI, createdAt);
        if (coveredKeys.has(purchaseKey)) continue;
        if (emittedKeys.has(purchaseKey)) continue;
        emittedKeys.add(purchaseKey);

        const matchingLine = lines.find((l) => {
          const line = l as { price?: { product?: string }; pricing?: { price_details?: { product?: string } } };
          return (
            line?.price?.product === pid ||
            line?.pricing?.price_details?.product === pid
          );
        }) as { description?: string; amount?: number } | undefined;

        myProductsPending.push({ pid, paymentIntent: invoicePI, matchingLine, createdAt, currency: invoice.currency ?? "usd" });
      }
    }

    // FCR Pass 13 Sakshi Issue 2 (additional): resolve in parallel for consistency.
    const myProductsResolved = await Promise.all(
      myProductsPending.map((x) =>
        resolveSyntheticProductDisplay(x.pid, x.matchingLine, x.currency),
      ),
    );

    myProductsPending.forEach((x, i) => {
      const { productName, interval, amount } = myProductsResolved[i];
      const accessEndDate = x.createdAt
        ? new Date(x.createdAt * 1000).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : undefined;
      syntheticRefundedRows.push({
        id: `refunded-${x.pid}-${x.paymentIntent || x.createdAt}`,
        productId: x.pid,
        productName,
        licenses: [],
        totalLicenses: 0,
        type: interval ? "recurring" : "one_time",
        interval,
        totalDevices: 0,
        availableDevices: 0,
        createdAt: x.createdAt,
        displayStatus: "expired",
        accessEndDate,
      });
    });
  }

  return [...activeResult, ...expiredEntries, ...syntheticRefundedRows] as SubscriptionProduct[];
};
